// TWO scopes: the ORG's branding (admins create/edit it; every member may USE it — non-admins see
// it read-only) and the user's PERSONAL branding.
import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import {
  effectiveBranding,
  normalizeLogo,
  saveBrandingUse,
  saveOrgBranding,
  savePersonalBranding,
  type Branding,
  type BrandingState,
} from "../../lib/branding";
import { useBranding } from "./useBranding";
import { useOrgStore } from "../../stores/orgStore";
import { useAuthStore } from "../../stores/authStore";
import { Modal } from "../../ui/Modal";

const field =
  "w-full rounded border border-line bg-canvas px-2 py-1.5 text-[12px] text-fg outline-none focus:border-accent";
const label = "mb-0.5 block text-[11px] font-medium text-muted";

function BrandingForm({
  value,
  disabled,
  onChange,
}: {
  value: Branding;
  disabled: boolean;
  onChange: (b: Branding) => void;
}) {
  const [logoErr, setLogoErr] = useState<string | null>(null);
  const pickLogo = async (file: File | undefined) => {
    if (!file) return;
    setLogoErr(null);
    try {
      onChange({ ...value, logo: await normalizeLogo(file) });
    } catch (e) {
      setLogoErr(e instanceof Error ? e.message : "Could not read that image.");
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <div>
        <label className={label}>Name</label>
        <input
          className={field}
          disabled={disabled}
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
          placeholder="Farm or business name"
        />
      </div>
      <div>
        <label className={label}>Address</label>
        <textarea
          className={`${field} min-h-[54px] resize-y`}
          disabled={disabled}
          value={value.address}
          onChange={(e) => onChange({ ...value, address: e.target.value })}
          placeholder={"Street\nCity, State ZIP\nphone / email"}
        />
      </div>
      <div>
        <label className={label}>Logo</label>
        <div className="flex items-center gap-2">
          {value.logo ? (
            <img
              src={value.logo}
              alt="logo preview"
              className="h-10 max-w-[120px] rounded border border-line bg-white object-contain p-0.5"
            />
          ) : (
            <span className="text-[11px] text-faint">none</span>
          )}
          {!disabled && (
            <>
              <label className="flex cursor-pointer items-center gap-1 rounded border border-line px-2 py-1 text-[11px] text-fg hover:bg-hover">
                <Upload size={11} /> {value.logo ? "Replace" : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => void pickLogo(e.target.files?.[0])}
                />
              </label>
              {value.logo && (
                <button
                  className="text-[11px] text-muted hover:text-red-400"
                  onClick={() => onChange({ ...value, logo: null })}
                >
                  Remove
                </button>
              )}
            </>
          )}
        </div>
        {logoErr && <p className="mt-1 text-[10px] text-red-400">{logoErr}</p>}
      </div>
    </div>
  );
}

const EMPTY: Branding = { name: "", address: "", logo: null };

export function BrandingModal({ onClose }: { onClose: () => void }) {
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const orgs = useAuthStore((s) => s.session?.orgs ?? []);
  const isAdmin = orgs.some(
    (o) => o.id === activeOrgId && o.role === "org_admin",
  );
  const [state, setState] = useState<BrandingState | null>(null);
  const [org, setOrg] = useState<Branding>(EMPTY);
  const [personal, setPersonal] = useState<Branding>(EMPTY);
  const [use, setUse] = useState<"org" | "personal">("org");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const brandingQuery = useBranding(activeOrgId);
  const seededFor = useRef<string | null>(null);

  useEffect(() => {
    const id = activeOrgId ?? "";
    if (seededFor.current === id) return;
    if (
      !brandingQuery.isSuccess ||
      brandingQuery.isPlaceholderData ||
      brandingQuery.isFetching ||
      !brandingQuery.data
    )
      return;
    seededFor.current = id;
    const st = brandingQuery.data;
    setState(st);
    setOrg(st.org ?? EMPTY);
    setPersonal(st.personal ?? EMPTY);
    setUse(st.use);
  }, [
    activeOrgId,
    brandingQuery.isSuccess,
    brandingQuery.isPlaceholderData,
    brandingQuery.isFetching,
    brandingQuery.data,
  ]);

  // So an untouched-and-still-empty scope is NOT written: saving the form would otherwise turn
  // "never set" into a stored blank record for every user who only came here to flip the
  // org/personal choice.
  const untouchedEmpty = (b: Branding, loaded: Branding | null): boolean =>
    loaded == null &&
    b.name.trim() === "" &&
    b.address.trim() === "" &&
    b.logo == null;

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      if (isAdmin && activeOrgId && !untouchedEmpty(org, state?.org ?? null))
        await saveOrgBranding(activeOrgId, org);
      if (!untouchedEmpty(personal, state?.personal ?? null))
        await savePersonalBranding(personal);
      await saveBrandingUse(use);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const radio = (val: "org" | "personal", title: string, note: string) => (
    <label className="flex cursor-pointer items-start gap-2">
      <input
        type="radio"
        name="branding-use"
        className="mt-0.5 accent-[var(--color-accent)]"
        checked={use === val}
        onChange={() => setUse(val)}
      />
      <span>
        <span className="block text-[12px] font-medium text-fg">{title}</span>
        <span className="block text-[10px] text-muted">{note}</span>
      </span>
    </label>
  );

  return (
    <Modal>
      <div
        className="fixed inset-0 z-dialog flex items-center justify-center bg-black/50 p-4"
        onClick={onClose}
      >
        <div
          className="max-h-[85vh] w-[440px] overflow-auto rounded-lg border border-line bg-surface p-4 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-[14px] font-semibold text-fg">
              Report branding
            </h2>
            <button
              className="rounded p-1 text-muted hover:bg-hover hover:text-fg"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
          {state == null ? (
            <div className="flex items-center gap-2 py-6 text-[12px] text-muted">
              <Loader2 size={13} className="animate-spin" /> Loading…
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 rounded-md border border-line bg-canvas/40 p-2.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-faint">
                  Which branding do your reports use?
                </span>
                {radio(
                  "org",
                  "Organization branding",
                  "The default — everyone in the org shares it.",
                )}
                {radio(
                  "personal",
                  "My branding",
                  "Your own logo, name and address.",
                )}
              </div>

              <div className="rounded-md border border-line bg-canvas/40 p-2.5">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-faint">
                    Organization branding
                  </span>
                  {!isAdmin && (
                    <span className="text-[10px] text-faint">
                      only org admins can edit
                    </span>
                  )}
                </div>
                <BrandingForm
                  value={org}
                  disabled={!isAdmin}
                  onChange={setOrg}
                />
              </div>

              <div className="rounded-md border border-line bg-canvas/40 p-2.5">
                <span className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-faint">
                  My branding
                </span>
                <BrandingForm
                  value={personal}
                  disabled={false}
                  onChange={setPersonal}
                />
              </div>

              {effectiveBranding({ org, personal, use }) == null && (
                <p className="text-[10px] leading-snug text-amber-500">
                  The choice above has no name set yet — the Branded Report
                  stays unavailable until it does.
                </p>
              )}

              <div className="flex items-center justify-end gap-2">
                {saved && (
                  <span className="text-[11px] text-emerald-400">Saved</span>
                )}
                <button
                  disabled={saving}
                  onClick={() => void save()}
                  className="flex items-center gap-1.5 rounded border border-accent bg-accent px-3 py-1.5 text-[12px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
                >
                  {saving && <Loader2 size={12} className="animate-spin" />}{" "}
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
